import { Input } from "@comps/ui/input";
import { Label } from "@comps/ui/label";
import { RadioGroup, RadioGroupItem } from "@comps/ui/radio";
import { ScrollArea } from "@comps/ui/scroll";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import lucideIcons, { IconLucide } from "./lucideIcons";
import saxIcons, { IconSax } from "./saxIcons";
import { TW_IconSelectorDefaultIconPanelItem, TW_IconSelectorDefaultIconPanelItemIcon, TW_IconSelectorDefaultIconPanelItemSelected, TW_IconSelectorDefaultIconsPanel, TW_IconSelectorPanel, TW_IconSelectorSearchInput } from "./twClasses";
import { CompozzIcon, IconType, LucideIconType, SaxIconType } from "@dto/CompozzIcon";


export function CIcon({ compozzIcon, color, className }: { compozzIcon: CompozzIcon, color?: string, className: string }) {
    if (compozzIcon.type === SaxIconType) {
        return <IconSax iconName={compozzIcon.name} color={color} className={className} />;
    } else if (compozzIcon.type === LucideIconType) {
        return <IconLucide iconName={compozzIcon.name} color={color} className={className} />;
    }
}

export function getRandomIconName(iconType: IconType = SaxIconType): string {
    if (iconType === SaxIconType) {
        const iconMap = saxIcons;
        const iconNames = Array.from(iconMap.keys());
        return iconNames[Math.floor(Math.random() * iconNames.length)];
    } else if (iconType === LucideIconType) {
        const iconMap = lucideIcons;
        const iconNames = Array.from(iconMap.keys());
        return iconNames[Math.floor(Math.random() * iconNames.length)];
    }
    return "";
}

export function getRandomIcon(iconType: IconType = SaxIconType): CompozzIcon {
    return { name: getRandomIconName(iconType), type: iconType };
}

export interface IconSelectorProps {
    icon: CompozzIcon;
    color: string;
    onChange: (icon: CompozzIcon) => void;
}

export function IconSelector({ icon, color, onChange }: IconSelectorProps) {

    const { t } = useTranslation();
    const [searchText, setSearchText] = useState<string>("");
    const [iconType, setIconType] = useState<IconType>(SaxIconType);

    useEffect(() => {
        setIconType(icon.type);
    }, [icon]);

    const iconMap = iconType === SaxIconType ? saxIcons : lucideIcons;

    function normalize(text: string) {
        return text.toLowerCase().replace(/ /g, "-");
    }

    function handleIconTypeChange(value: string) {
        setSearchText("");
        setIconType(value as IconType);
    }

    const filteredIconNames = Array.from(iconMap.keys()).filter((iconName) => normalize(iconName).includes(normalize(searchText)));
    return (
        <div className={TW_IconSelectorPanel}>
            <div className="flex flex-row justify-between items-center gap-2 w-full p-2">
                <Label>{t("icon.selector.type.label")}</Label>
                <RadioGroup value={iconType} className="w-full flex flex-row gap-4 justify-evenly items-center grow" onValueChange={handleIconTypeChange}>
                    <div className="flex flex-row gap-2 items-center">
                        <RadioGroupItem value={SaxIconType} className="w-3 h-3">Iconsax</RadioGroupItem>
                        <Label className="text-sm">Iconsax</Label>
                    </div>
                    <div className="flex flex-row gap-2 items-center">
                        <RadioGroupItem value={LucideIconType} className="w-3 h-3">Lucide</RadioGroupItem>
                        <Label className="text-sm">Lucide</Label>
                    </div>
                </RadioGroup>
            </div>
            <Input className={TW_IconSelectorSearchInput} placeholder={t("icon.selector.search.input.placeholder")} onChange={(event) => setSearchText(event.target.value)} autoFocus={false} value={searchText} />

            <ScrollArea className="h-full w-full">
                <div className={TW_IconSelectorDefaultIconsPanel}>
                    {filteredIconNames.map((iconName) => {
                        const isSelected = iconName === icon.name && iconType === icon.type;
                        return (
                            <div key={iconName} className={TW_IconSelectorDefaultIconPanelItem + " " + (isSelected ? TW_IconSelectorDefaultIconPanelItemSelected : "")} onClick={() => onChange({ name: iconName, type: iconType })}>
                                {(iconType === "iconsax") ? <IconSax className={TW_IconSelectorDefaultIconPanelItemIcon} iconName={iconName} color={color} /> : <IconLucide className={TW_IconSelectorDefaultIconPanelItemIcon} iconName={iconName} color={color} />}
                            </div>
                        )
                    })}
                </div>
            </ScrollArea>

        </div>
    )
}